import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { DeviceRotation } from '@shure/shared/angular/data-access/system-api/models';
import { PositionUnit } from '@shure/shared/angular/utils/utils-position';

import { CoverageDeviceLocalRotation } from '../api/models';

import { DCA_DEVICE_LOCAL_ROTATION_DEFAULTS, deviceOrientationToDeviceLocalRotation } from './dca-device-orientation';

const DEFAULT_HEIGHT_RAW = 2743;

/**
 * FE stand-in for SysAPI `features.orientation` / device `AUDIO:DEVICE_ORIENTATION`.
 */
export interface DcaShimmedFeatures {
	readonly deviceLocalRotation: CoverageDeviceLocalRotation;
	readonly height: PositionUnit;
}

export const DCA_SHIMMED_FEATURES_DEFAULTS: DcaShimmedFeatures = {
	deviceLocalRotation: DCA_DEVICE_LOCAL_ROTATION_DEFAULTS,
	height: PositionUnit.fromRaw(DEFAULT_HEIGHT_RAW)
};

// TODO: remove when DCA901 supports height + orientation (DNG-36929).
@Injectable({ providedIn: 'root' })
export class DcaDeviceFeaturesShim {
	private readonly devices = new BehaviorSubject<Map<string, DcaShimmedFeatures>>(new Map());

	public has(deviceId: string): boolean {
		return this.devices.value.has(deviceId);
	}

	public register(deviceId: string): void {
		if (!this.devices.value.has(deviceId)) {
			this.update(deviceId, {});
		}
	}

	public getState$(deviceId: string): Observable<DcaShimmedFeatures> {
		return this.devices.pipe(map((m) => m.get(deviceId) ?? DCA_SHIMMED_FEATURES_DEFAULTS));
	}

	public setDeviceLocalRotation(deviceId: string, rotation: DeviceRotation): void {
		this.update(deviceId, {
			deviceLocalRotation: deviceOrientationToDeviceLocalRotation(rotation)
		});
	}

	public setHeight(deviceId: string, height: PositionUnit): void {
		this.update(deviceId, { height });
	}

	private update(deviceId: string, partial: Partial<DcaShimmedFeatures>): void {
		const current = this.devices.value.get(deviceId) ?? DCA_SHIMMED_FEATURES_DEFAULTS;
		const updated = new Map(this.devices.value);
		updated.set(deviceId, { ...current, ...partial });
		this.devices.next(updated);
	}
}
